import type { AgentProvider, ServerMsg } from './provider';
import { serializeForAgent } from './serialize-for-agent';
import { useCanvasStore } from '../store/canvas-store';
import { buildNodeOutline, htmlFrameIds } from './nodeOutline';
import { executeToolUse } from './executeToolUse';
import type { AgentStep } from './executeToolUse';

export type { AgentStep } from './executeToolUse';

interface Args {
  provider: AgentProvider;
  prompt: string;
  onText?: (delta: string) => void;
  onStep?: (step: AgentStep) => void;
  onError?: (msg: string) => void;
  /** Fired once the prompt is sent and we're awaiting the model. */
  onThinking?: () => void;
  /** Abort the turn: stops dispatching, resolves the promise promptly. */
  signal?: AbortSignal;
  followCamera?: boolean;
  /** Override the snapshot sent to the model (e.g. anchor-scoped). Defaults to
   *  a full serialization of the current canvas. */
  snapshot?: string;
  /** When set, frames the agent creates are translated as a group so the first
   *  one lands at this world point — keeps output near the chat node instead of
   *  wherever the model picked. */
  placeOrigin?: { x: number; y: number };
}

export async function runAgent(args: Args): Promise<void> {
  const { provider, prompt, onText, onStep, onError, onThinking, signal, followCamera = true, placeOrigin } = args;
  await provider.open();
  // Delta applied to placement commands, computed from the first created frame
  // so the whole composition shifts together (preserves relative layout).
  const placeState = { delta: null as { dx: number; dy: number } | null };
  await new Promise<void>((resolve) => {
    if (signal?.aborted) { resolve(); return; }
    let aborted = false;
    if (signal) {
      signal.addEventListener('abort', () => { aborted = true; resolve(); }, { once: true });
    }
    provider.onMessage(async (m: ServerMsg) => {
      if (aborted) return;
      if (m.type === 'text') onText?.(m.delta);
      else if (m.type === 'step') {
        // Server-side knowledge tool step (e.g. "searched design knowledge: …").
        // No AgentStep shape exists for informational display — surface via onText.
        onText?.(`\n[${m.label}]\n`);
      } else if (m.type === 'tool_use') {
        await executeToolUse(provider, m, { followCamera, placeOrigin, placeState, onStep });
      } else if (m.type === 'done') {
        resolve();
      } else if (m.type === 'error') {
        onError?.(m.message);
        resolve();
      }
    });
    const state = useCanvasStore.getState();
    let snapshot = args.snapshot;
    if (snapshot === undefined) {
      const outline = buildNodeOutline(state, htmlFrameIds(state));
      const base = serializeForAgent(state);
      snapshot = outline ? `${base}\n${outline}` : base;
    }
    provider.send({ type: 'user', prompt, snapshot, history: [] });
    onThinking?.();
  });
}

